"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, Check, Mail, Github, Linkedin, Twitter } from "lucide-react";

export default function CTASection() {
    const [copied, setCopied] = useState(false);
    const email = "hasan.w.mustafa@gmail.com";

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(email);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const socialLinks = [
        { name: "GitHub", icon: <Github className="w-5 h-5" />, url: "https://github.com/HasanWael" },
        { name: "LinkedIn", icon: <Linkedin className="w-5 h-5" />, url: "https://www.linkedin.com/in/hassan-kamel-948198225/" },
    ];

    return (
        <section
            id="contact"
            className="section-snap flex items-center justify-center bg-background min-h-[60vh] py-20"
        >
            <div className="container mx-auto px-section-x text-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl mx-auto"
                >
                    <h2
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight"
                        style={{ fontFamily: "var(--font-playfair), Georgia, serif" }}
                    >
                        Let&apos;s build something <br />
                        <span className="text-accent">amazing together.</span>
                    </h2>

                    <p className="text-xl text-text-secondary mb-10 max-w-2xl mx-auto">
                        I&apos;m currently available for freelance projects and open to new opportunities.
                        If you have a project that needs some creative touch, I&apos;d love to hear about it.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        {/* Email Split Button */}
                        <div className="relative z-10">
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="flex bg-accent text-white rounded-full transition-all duration-200 group shadow-lg hover:shadow-xl active:shadow-xl"
                            >
                                {/* Mail Action (Primary) */}
                                <a
                                    href={`mailto:${email}`}
                                    className="flex items-center gap-3 px-8 py-4 hover:bg-accent-hover active:bg-accent-hover transition-colors font-semibold text-lg rounded-l-full"
                                    aria-label="Send Email"
                                >
                                    <Mail className="w-5 h-5" />
                                    Send Email
                                </a>

                                {/* Divider */}
                                <div className="py-4">
                                    <div className="w-[1px] h-full bg-white/20" />
                                </div>

                                {/* Copy Email Action (Secondary) */}
                                <div className="relative">
                                    <button
                                        onClick={handleCopyEmail}
                                        className="cursor-pointer flex items-center justify-center px-6 h-full hover:bg-accent-hover active:bg-accent-hover transition-colors peer rounded-r-full"
                                        aria-label="Copy Email Address"
                                    >
                                        {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                                    </button>

                                    {/* Tooltip */}
                                    <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 text-xs font-medium rounded-md pointer-events-none opacity-0 -translate-y-2 peer-hover:opacity-100 peer-hover:translate-y-0 transition-all duration-200 whitespace-nowrap shadow-lg z-50 bg-foreground text-background">
                                        {copied ? "Copied!" : "Copy email"}
                                        {/* Arrow */}
                                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-foreground" />
                                    </span>
                                </div>
                            </motion.div>

                            {/* Helper Text (Only shows when copied) */}
                            <AnimatePresence>
                                {copied && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className="absolute left-1/2 -translate-x-1/2 -bottom-10 text-sm text-text-secondary font-medium whitespace-nowrap bg-background px-3 py-1 rounded-full shadow-sm border border-border"
                                    >
                                        {email}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Social Icons */}
                        <div className="flex items-center gap-2 mt-4 sm:mt-0 sm:ml-4">
                            {socialLinks.map((link) => (
                                <motion.a
                                    key={link.name}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                    whileTap={{ scale: 0.9 }}
                                    className="w-12 h-12 flex items-center justify-center rounded-full bg-surface text-foreground hover:bg-background active:bg-background hover:text-accent active:text-accent hover:shadow-md active:shadow-md transition-all duration-300 border border-transparent hover:border-border active:border-border"
                                    aria-label={link.name}
                                >
                                    {link.icon}
                                </motion.a>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
